import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { IApiService } from 'core/api/services/api.interface';
import { UserApiService } from 'features/user/api/api-tokens';

/**
 * Result of resolving a card BIN against the Freely Card BIN range.
 * `isFreely` is the only field the Collect flow strictly needs; `percent`
 * and `label` are carried so the rate/label remain data-driven via the
 * BIN range metadata (falling back to the product defaults of 1% / "Freely Card").
 */
export interface FreelyCardResolution {
  isFreely: boolean;
  percent: number;
  label: string;
}

const FREELY_DEFAULT_PERCENT = 1;
const FREELY_DEFAULT_LABEL = 'Freely Card';

const NOT_FREELY: FreelyCardResolution = {
  isFreely: false,
  percent: FREELY_DEFAULT_PERCENT,
  label: FREELY_DEFAULT_LABEL,
};

/**
 * Recognizes the distributor's "Freely Card" (a Mastercard brand we issue)
 * by looking its BIN up against the shared BIN-range system. The lookup is
 * cached per BIN so repeatedly re-selecting the same card does not re-hit the API.
 */
@Injectable({ providedIn: 'root' })
export class FreelyCardService {
  private readonly cache = new Map<string, Observable<FreelyCardResolution>>();

  constructor(@Inject(UserApiService) private apiService: IApiService) {}

  /**
   * Resolves whether the given BIN belongs to the Freely Card range.
   * Returns a non-Freely result for missing/short BINs or when no range matches.
   */
  resolve(bin: string | null | undefined): Observable<FreelyCardResolution> {
    const normalized = (bin ?? '').toString().replace(/\D/g, '');
    if (normalized.length < 6) {
      return of(NOT_FREELY);
    }

    const cached = this.cache.get(normalized);
    if (cached) {
      return cached;
    }

    const request$ = this.apiService
      .get<{ binName?: string; cardType?: string; metadata?: Record<string, any> }>('freelyBinLookup', {
        urlParams: { number: normalized },
      })
      .pipe(
        map((match) => this.toResolution(match)),
        // A 404 (no matching BIN range) or any error simply means "not Freely".
        catchError(() => of(NOT_FREELY)),
        shareReplay(1)
      );

    this.cache.set(normalized, request$);
    return request$;
  }

  private toResolution(match: { metadata?: Record<string, any> } | null): FreelyCardResolution {
    const metadata = match?.metadata;
    if (!metadata || metadata['isFreelyCard'] !== true) {
      return NOT_FREELY;
    }
    return {
      isFreely: true,
      percent: typeof metadata['costOfCapitalPercent'] === 'number' ? metadata['costOfCapitalPercent'] : FREELY_DEFAULT_PERCENT,
      label: typeof metadata['displayLabel'] === 'string' && metadata['displayLabel'] ? metadata['displayLabel'] : FREELY_DEFAULT_LABEL,
    };
  }
}
